import React, { useState, forwardRef, useImperativeHandle } from "react";
import { FiFolder } from "react-icons/fi";
import { Project, TodoItem } from "../../lib/adhd-todo-manager";
// import { ProjectCard } from './ProjectCard';
// import { TodoCard } from './TodoCard';
import { UnassignedTasksDropZone } from "./UnassignedTasksDropZone";
import styles from "./CollapsibleProjectTree.module.scss";

interface CollapsibleProjectTreeProps {
  projects: Project[];
  todos: TodoItem[];
  onTodoUpdate?: (todo: TodoItem) => void;
  onTodoEdit?: (todo: TodoItem) => void;
  onStartBodyDoubling?: (todo: TodoItem) => void;
  onMoveToProject?: (todoId: string, projectId: string | null) => void;
}

export interface CollapsibleProjectTreeRef {
  expandProject: (projectId: string) => void;
  collapseProject: (projectId: string) => void;
  expandAllProjects: () => void;
  collapseAllProjects: () => void;
  focusProject: (projectId: string) => void;
  focusTodo: (todoId: string) => void;
}

export const CollapsibleProjectTree = forwardRef<
  CollapsibleProjectTreeRef,
  CollapsibleProjectTreeProps
>(({ projects, todos, onTodoUpdate, onTodoEdit, onStartBodyDoubling, onMoveToProject }, ref) => {
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());

  // Group todos by project
  const unassignedTodos = todos.filter(todo => !todo.project_id);

  // const toggleProject = (projectId: string) => {
  //   setExpandedProjects(prev => {
  //     const newSet = new Set(prev);
  //     if (newSet.has(projectId)) {
  //       newSet.delete(projectId);
  //     } else {
  //       newSet.add(projectId);
  //     }
  //     return newSet;
  //   });
  // };

  // Expose imperative methods for voice commands
  useImperativeHandle(
    ref,
    () => ({
      expandProject: (projectId: string) => {
        setExpandedProjects(prev => new Set([...prev, projectId]));
      },
      collapseProject: (projectId: string) => {
        setExpandedProjects(prev => {
          const newSet = new Set(prev);
          newSet.delete(projectId);
          return newSet;
        });
      },
      expandAllProjects: () => {
        setExpandedProjects(new Set(projects.map(p => p.id)));
      },
      collapseAllProjects: () => {
        setExpandedProjects(new Set());
      },
      focusProject: (projectId: string) => {
        // Could implement focus behavior here
        console.log("Focus project:", projectId);
      },
      focusTodo: (todoId: string) => {
        // Could implement focus behavior here
        console.log("Focus todo:", todoId);
      },
    }),
    [projects]
  );

  return (
    <div className={styles.treeContainer}>
      <div className={styles.gridContainer}>
        {/* Projects Grid */}
        <div className={styles.projectsGrid}>
          {projects.map(project => {
            const projectTodos = todos.filter(todo => todo.project_id === project.id);
            const isExpanded = expandedProjects.has(project.id);

            return (
              <div key={project.id} className={styles.projectSection}>
                {/* Project Card - Temporarily disabled */}
                <div>
                  Project: {project.name} ({projectTodos.length} tasks)
                </div>

                {/* Project Todos Grid */}
                {isExpanded && projectTodos.length > 0 && (
                  <div className={styles.todosGrid}>
                    {projectTodos.map(todo => (
                      <div key={todo.id}>
                        Task: {todo.title || todo.description || "Untitled task"}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Unassigned Todos Section */}
        {unassignedTodos.length > 0 && (
          <div className={styles.unassignedSection}>
            <UnassignedTasksDropZone
              taskCount={unassignedTodos.length}
              onMoveToProject={onMoveToProject}
            />
            <div className={styles.todosGrid}>
              {unassignedTodos.map(todo => (
                <div key={todo.id}>
                  Unassigned: {todo.title || todo.description || "Untitled task"}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {projects.length === 0 && unassignedTodos.length === 0 && (
          <div className={styles.emptyState}>
            <FiFolder className={styles.emptyIcon} />
            <h3 className={styles.emptyTitle}>No tasks yet</h3>
            <p className={styles.emptyDescription}>
              Create your first project or task to get started with your ADHD assistant
            </p>
          </div>
        )}
      </div>
    </div>
  );
});

CollapsibleProjectTree.displayName = "CollapsibleProjectTree";
